import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { supabase } from "./supabaseClient";
import { logEvent } from "./history";
import {
  Container, Row, Col, Card, Badge, Button,
  Spinner, ListGroup, ProgressBar, Alert, Form
} from "react-bootstrap";

import {
  ArrowLeft, Calendar3, Person, Files,
  ChatLeftText, Globe2, PlusLg, Save, ClockHistory
} from "react-bootstrap-icons";
import HistoryModal from "./components/history/HistoryModal";
import TargetLocalesSelector from "./components/locales/TargetLocalesSelector";

export default function JobDetail() {

  const { requestNumber, jobId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [job, setJob] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [showExtraLocales, setShowExtraLocales] = useState(false);
  const [historyContext, setHistoryContext] = useState({
    entityType: null,
    entityId: null,
    title: "",
  });
  const [showHistory, setShowHistory] = useState(false);

  const [jobData, setJobData] = useState({
    deadline: "",
    notes: "",
    assets_url: "",
    context_notes: "",
    delivery_instructions: "",
    target_languages: []
  });

  useEffect(() => {

    async function fetchJobData() {

      setLoading(true);

      try {

        const { data: request } = await supabase
          .from("requests")
          .select("id")
          .eq("request_number", requestNumber)
          .single();

        const { data, error } = await supabase
          .from("jobs")
          .select(`
*,
requests (
title,
description,
source_language,
target_languages,
attachments,
projects(name)
)
`)
          .eq("request_id", request.id)
          .eq("job_number", jobId)
          .single();

        if (error) throw error;

        setJob(data);

        setJobData({
          deadline: data.deadline || "",
          notes: data.notes || "",
          assets_url: data.assets_url || "",
          context_notes: data.context_notes || "",
          delivery_instructions: data.delivery_instructions || "",
          target_languages: data.target_languages_ids || data.requests.target_languages || []
        });

        const { data: taskData } = await supabase
          .from("tasks")
          .select(`
          *,
          vendors (
            name
          )
        `)
          .eq("job_id", data.id);

        setTasks(taskData || []);

        const { data: langs } = await supabase
          .from("languages")
          .select("id,name,flag,region")
          .order("region");

        setLanguages(langs || []);

      } catch (err) {

        console.error(err);

      } finally {

        setLoading(false);

      }

    }

    fetchJobData();

  }, [jobId, requestNumber]);

  async function saveJob() {

    await supabase
      .from("jobs")
      .update({
        deadline: jobData.deadline,
        notes: jobData.notes,
        assets_url: jobData.assets_url,
        context_notes: jobData.context_notes,
        delivery_instructions: jobData.delivery_instructions,
        target_languages_ids: jobData.target_languages
      })
      .eq("id", job.id);

    await logEvent({
      entityType: "job",
      entityId: job.id,
      action: "custom",
      summary: "Job details updated",
      details: {
        deadline: jobData.deadline,
        notes: jobData.notes,
        assets_url: jobData.assets_url,
        context_notes: jobData.context_notes,
        delivery_instructions: jobData.delivery_instructions,
        target_languages: jobData.target_languages,
      },
    });

    alert("Job saved");

  }

  const targetLanguageIds = jobData.target_languages || [];

  const setTargetLanguageIds = (updater) => {
    setJobData(prev => {
      const prevIds = prev.target_languages || [];
      const nextIds = typeof updater === "function" ? updater(prevIds) : updater;
      return { ...prev, target_languages: nextIds };
    });
  };

  const filteredLocales = useMemo(() => languages, [languages]);

  const groupedLocales = useMemo(() => {
    const list = showExtraLocales ? languages : filteredLocales;
    return list.reduce((acc, lang) => {
      const region = lang.region || "Other";
      if (!acc[region]) acc[region] = [];
      acc[region].push(lang);
      return acc;
    }, {});
  }, [languages, filteredLocales, showExtraLocales]);

  if (loading) return <div className="text-center mt-5"><Spinner animation="border" /></div>;
  if (!job) return <Container className="mt-5"><Alert variant="danger">Job not found</Alert></Container>;

  async function generateTasks() {

    try {

      const { error } = await supabase.rpc("kickoff_job", {
        target_job_id: job.id
      });

      if (error) throw error;

      await logEvent({
        entityType: "job",
        entityId: job.id,
        action: "custom",
        summary: "Tasks generated",
        details: {
          job_id: job.id,
          languages: jobData.target_languages
        }
      });

      const { data: taskData, error: taskError } = await supabase
        .from("tasks")
        .select(`
    *,
    vendors (
      name
    )
  `)
        .eq("job_id", job.id);

      if (taskError) throw taskError;

      setTasks(taskData || []);

      alert("Tasks generated");

    } catch (err) {

      console.error(err);
      alert("Failed to generate tasks");

    }

  }
  return (

    <>
      <Container fluid className="py-4 px-lg-5" style={{ maxWidth: "1800px" }}>

        {/* HEADER */}

        <div className="d-flex justify-content-between align-items-start mb-4">

          <div>

            <Button
              variant="link"
              className="p-0 text-decoration-none mb-2"
              onClick={() => navigate(`/request/${requestNumber}`)}
            >
              <ArrowLeft /> Back to Request
            </Button>

            <h2 className="fw-bold d-flex align-items-center">
              {job.job_title}
              <Badge bg="primary" className="ms-3 fs-6">Job #{job.job_number}</Badge>
            </h2>

            <div className="text-muted">
              <span className="me-3"><Person size={14} /> PM: System</span>
              <span><Calendar3 size={14} /> Created: {new Date(job.created_at).toLocaleDateString()}</span>
            </div>

          </div>

          <div className="text-end">

            <div className="mb-2 d-flex justify-content-end gap-2">
              <Badge bg={job.status === "in_progress" ? "success" : "secondary"} className="px-3 py-2 text-uppercase">
                {job.status.replace("_", " ")}
              </Badge>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setHistoryContext({
                    entityType: "job",
                    entityId: job.id,
                    title: `Job #${job.job_number} History`,
                  });
                  setShowHistory(true);
                }}
              >
                <ClockHistory className="me-1" /> History
              </Button>
            </div>

            <div className="mt-1 fw-bold text-primary">
              Deadline: {jobData.deadline ? new Date(jobData.deadline).toLocaleString() : "N/A"}
            </div>

            <Button className="mt-3" variant="success" onClick={saveJob}>
              <Save className="me-2" /> Save Job
            </Button>

          </div>

        </div>

        {/* METRICS */}

        <Row className="mb-4">

          <Col md={3}>
            <Card className="text-center shadow-sm border-0">
              <Card.Body>
                <h4>{jobData.target_languages.length}</h4>
                <small className="text-muted">Locales</small>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3}>
            <Card className="text-center shadow-sm border-0">
              <Card.Body>
                <h4>{tasks.length}</h4>
                <small className="text-muted">Tasks</small>
              </Card.Body>
            </Card>
          </Col>

        </Row>

        <Row className="g-4">

          {/* LEFT COLUMN */}

          <Col lg={8}>

            {/* LOCALE MANAGEMENT */}

            <TargetLocalesSelector
              groupedLocales={groupedLocales}
              filteredLocales={filteredLocales}
              targetLanguageIds={targetLanguageIds}
              setTargetLanguageIds={setTargetLanguageIds}
              showExtraLocales={showExtraLocales}
              setShowExtraLocales={setShowExtraLocales}
            />

            {/* TASKS */}

            <Card className="shadow-sm border-0 rounded-4 mb-4">

              <Card.Header className="bg-white border-0 pt-4 px-4 d-flex justify-content-between">

                <h5 className="fw-bold mb-0"><Globe2 className="me-2" /> Linguistic Tasks</h5>

                {tasks.length === 0 &&
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={generateTasks} // Add this line
                    disabled={loading}       // Optional: disable while processing
                  >
                    {loading ? <Spinner size="sm" /> : <PlusLg className="me-1" />} Generate Tasks
                  </Button>
                }

              </Card.Header>

              <Card.Body className="p-4">

                {tasks.length > 0 ?

                  <table className="table align-middle">

                    <thead className="small text-muted text-uppercase">
                      <tr>
                        <th>Language</th>
                        <th>Vendor</th>
                        <th>Status</th>
                        <th>Progress</th>
                        <th>History</th>
                      </tr>
                    </thead>

                    <tbody>

                      {tasks.map(task => {

                        const lang = languages.find(l => l.id === task.target_language_id);

                        return (

                          <tr key={task.id}>

                            <td>{lang?.flag} {lang?.name}</td>
                            <td>
        <span className="fw-semibold text-dark">
          {task.vendors?.name || (
            <span className="text-muted italic small">Unassigned</span>
          )}
        </span>
      </td>
                            <td>
                              <Badge bg="light" text="dark" className="border">
                                {task.status || "pending"}
                              </Badge>
                            </td>

                            <td style={{ width: "150px" }}>
                              <ProgressBar now={task.progress || 0} style={{ height: "6px" }} />
                            </td>

                            <td>
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-decoration-none"
                                onClick={() => {
                                  setHistoryContext({
                                    entityType: "task",
                                    entityId: task.id,
                                    title: `Task History (${lang?.name || "Unknown language"})`,
                                  });
                                  setShowHistory(true);
                                }}
                              >
                                <ClockHistory size={16} /> History
                              </Button>
                            </td>

                          </tr>

                        )

                      })}

                    </tbody>

                  </table>

                  :

                  <div className="text-center py-5 border rounded bg-light">
                    No tasks generated yet
                  </div>

                }

              </Card.Body>

            </Card>

            {/* NOTES */}

            <Card className="shadow-sm border-0 rounded-4">

              <Card.Body className="p-4">

                <h5 className="fw-bold mb-3"><ChatLeftText className="me-2" /> Production Notes</h5>

                <Form.Control
                  as="textarea"
                  rows={4}
                  value={jobData.notes}
                  onChange={e => setJobData({ ...jobData, notes: e.target.value })}
                />

              </Card.Body>

            </Card>

          </Col>

          {/* RIGHT COLUMN */}

          <Col lg={4}>

            <Card className="shadow-sm border-0 rounded-4 mb-4 bg-dark text-white">

              <Card.Body className="p-4">

                <h6 className="text-uppercase small opacity-75 mb-3">
                  Request Reference
                </h6>

                <h5 className="fw-bold mb-1">
                  {job.requests?.projects?.name}
                </h5>

                <p className="small opacity-75">
                  {job.requests?.description}
                </p>

                <hr className="opacity-25" />

                <div className="d-flex justify-content-between">
                  <span className="small opacity-75">Original Locales</span>
                  <span className="small fw-bold">
                    {job.requests?.target_languages?.length}
                  </span>
                </div>

              </Card.Body>

            </Card>

            <Card className="shadow-sm border-0 rounded-4">

              <Card.Body className="p-4">

                <h6 className="text-uppercase small text-muted mb-3 fw-bold">
                  <Files className="me-2" /> Source Assets
                </h6>

                <ListGroup variant="flush">

                  {job.requests?.attachments?.length > 0 ?

                    job.requests.attachments.map((file, idx) => (

                      <ListGroup.Item
                        key={idx}
                        className="px-0 py-3 bg-transparent border-bottom d-flex justify-content-between"
                      >

                        <div>

                          <div className="small fw-bold">
                            {file.name || `Asset ${idx + 1}`}
                          </div>

                          <div className="x-small text-muted">
                            {(file.size / 1024).toFixed(1)} KB
                          </div>

                        </div>

                        <Button
                          size="sm"
                          variant="outline-primary"
                          href={file.url}
                          target="_blank"
                        >
                          Download
                        </Button>

                      </ListGroup.Item>

                    ))

                    :

                    <div className="text-muted small">No attachments</div>

                  }

                </ListGroup>

              </Card.Body>

            </Card>

          </Col>

        </Row>

        <style>{`
.x-small{font-size:0.7rem;}
`}</style>

      </Container>

      <HistoryModal
        entityType={historyContext.entityType}
        entityId={historyContext.entityId}
        title={historyContext.title}
        show={showHistory}
        onHide={() => setShowHistory(false)}
      />
    </>

  );

}